<template>
    <div>
        <div
            v-for="group in groups"
            :key="group.connection || 'ungrouped'"
            :class="group.connection ? 'border rounded p-3 mb-4' : 'mb-2'"
        >
            <template v-if="group.connection">
                <h6 class="mb-1">
                    {{ group.name }}
                </h6>
                <p
                    v-if="group.description"
                    class="text-medium-emphasis small"
                >
                    {{ group.description }}
                </p>
                <hr class="mt-0 mb-3">
            </template>

            <DFormRow
                v-for="option in group.options"
                :key="option.name"
                :label="option.description || option.name"
                :is-required="option.required"
                :error="errorFor(option.name)"
                :hint="hintFor(option)"
            >
                <DSwitch
                    v-if="option.type === 'bool'"
                    :id="fieldId(option.name)"
                    :checked="!!values[option.name]"
                    color="success"
                    variant="3d"
                    :class="{'is-invalid': !!errorFor(option.name)}"
                    @update:checked="set(option.name, $event)"
                />
                <DFormTextarea
                    v-else-if="option.type === 'text'"
                    :id="fieldId(option.name)"
                    :value="values[option.name]"
                    :is-valid="validity(option.name)"
                    rows="4"
                    @update:modelValue="set(option.name, $event)"
                />
                <DSelect
                    v-else-if="option.type === 'list'"
                    :id="fieldId(option.name)"
                    :value="values[option.name]"
                    :options="option.possible_values"
                    :is-valid="validity(option.name)"
                    @update:value="set(option.name, $event)"
                />
                <DInput
                    v-else
                    :id="fieldId(option.name)"
                    :value="values[option.name]"
                    :type="option.encrypted ? 'password' : 'text'"
                    :placeholder="placeholderFor(option)"
                    :is-valid="validity(option.name)"
                    autocomplete="off"
                    @update:value="set(option.name, $event)"
                />
            </DFormRow>

            <div
                v-if="group.connection && testRoute"
                style="display: flex; grid-gap: 15px; align-items: center;"
                class="mb-3"
            >
                <DButton
                    icon="lan-connect"
                    :label="__('products.fulfillmentAccountOptions.test_connection')"
                    :disabled="isTesting(group.connection)"
                    class="text-nowrap"
                    @click="testConnection(group.connection)"
                />
                <div
                    v-if="testResult(group.connection) === true"
                    style="color: var(--colorSuccess);"
                >
                    {{ __('products.fulfillmentAccountOptions.connection_ok') }}
                </div>
                <div
                    v-else-if="testResult(group.connection) === false"
                    style="color: var(--colorDanger);"
                >
                    {{ testMessage(group.connection) || __('products.fulfillmentAccountOptions.connection_failed') }}
                </div>
            </div>
        </div>
    </div>
</template>

<script>
export default {
    name: 'AccountOptionFields',
    props: {
        options: {
            type: Array,
            default: () => []
        },
        values: {
            type: Object,
            default: () => ({})
        },
        errors: {
            type: Object,
            default: () => ({})
        },
        errorPrefix: {
            type: String,
            default: 'account_options'
        },
        tier: {
            type: String,
            default: 'group'
        },
        testRoute: {
            type: String,
            default: null
        }
    },
    data () {
        return {
            tests: {}
        }
    },
    computed: {
        groups () {
            const map = new Map()

            this.options.forEach((option) => {
                const key = option.connection || ''

                if (!map.has(key)) {
                    map.set(key, [])
                }

                map.get(key).push(option)
            })

            return Array.from(map.entries()).map(([connection, options]) => ({
                connection: connection || null,
                name: options[0]?.connection_name || connection || null,
                description: options[0]?.connection_description || null,
                options
            }))
        }
    },
    methods: {
        fieldId (name) {
            return `account-option-${name}`
        },
        errorFor (name) {
            return this.errors[`${this.errorPrefix}.${name}`]
        },
        validity (name) {
            return this.errorFor(name) ? false : null
        },
        set (name, value) {
            this.values[name] = value
        },
        placeholderFor (option) {
            if (option.encrypted && option.has_value) {
                return this.__('products.fulfillmentAccountOptions.secret_set')
            }

            return option.description || option.name
        },
        hintFor (option) {
            if (this.tier === 'account' && option.source === 'group') {
                return this.__('products.fulfillmentAccountOptions.inherited_from_group')
            }

            if (option.encrypted && option.has_value) {
                return this.__('products.fulfillmentAccountOptions.secret_keep_blank')
            }

            return null
        },
        isTesting (connection) {
            return this.tests[connection]?.loading === true
        },
        testResult (connection) {
            return this.tests[connection]?.success ?? null
        },
        testMessage (connection) {
            return this.tests[connection]?.message ?? null
        },
        testConnection (connection) {
            const self = this
            this.tests[connection] = { loading: true, success: null, message: null }

            window.axios.post(this.testRoute, { connection, account_options: this.values })
                .then(function (response) {
                    self.tests[connection] = { loading: false, success: response.data.success === true, message: null }
                })
                .catch(function (error) {
                    self.tests[connection] = {
                        loading: false,
                        success: false,
                        message: error.response?.data?.message ?? null
                    }
                })
        }
    }
}
</script>
